import { DEVICE_TECHNOLOGY } from '@/constants/amc';
import { PLATFORMS } from '@/constants/app';
import { useAppConfigContext } from '@/context/AppContext';
import { useProductContext } from '@/context/productContext';
import { profileType, useProfileContext } from '@/context/profileContext';
import { MapType } from '@/types';
import { AssetsData } from '@/types/asset';
import Cookies from '@/utils/cookie';
import is from '@/utils/is';
import SessionStorage from '@/utils/sessionStorage';
import { useCallback, useMemo } from 'react';
import useWebEngage from './useWebEngage';
import { removeUndefinedKeys } from '@/utils/object';

export const getTrafficTrackingProperties = () => {
  return removeUndefinedKeys({
    utm_source: Cookies.get('utm_source'),
    utm_medium: Cookies.get('utm_medium'),
    utm_channel: Cookies.get('utm_channel'),
    utm_campaign: Cookies.get('utm_campaign'),
    utm_term: Cookies.get('utm_term'),
  });
};

export const getScreenProperties = () => {
  return {
    screen_url: window.location.href,
  };
};

export const pushEventToDataLayer = (eventProperties: any): void => {
  if (is.client() && eventProperties.event) {
    if (is.dev()) {
      console.log('GTM EVENT: ' + eventProperties.event + ' triggered!', eventProperties);
    }
    window.dataLayer?.push(eventProperties);
  }
};

const DEFAULT_EVENT_PROPERTIES: MapType = {
  design_rehash_journey: true,
};

export const getCustomerProperties = (profile: profileType) => {
  const { customerId, isLoggedIn } = profile;
  const customerProperties: any = {
    logged_in: isLoggedIn,
  };

  if (customerId) {
    customerProperties.customerId = customerId;
  }

  return customerProperties;
};

const getSelectedDeviceProperties = (selectedProduct: AssetsData) => {
  if (selectedProduct) {
    const { id, isRental, segment, technology, expiryStatus, currentPlan, img, name, componentId } =
      selectedProduct;

    return {
      product_name: name,
      product_code: componentId,
      product_segment: segment?.toLowerCase(),
      product_technology: technology,
      asset_id: id,
      //   asset_type: '',
      is_rental: isRental,
      rental_renewal_date: '',
      amc_status: expiryStatus?.status,
      amc_due_date: currentPlan?.endDate,
      //   is_default: '',
      product_image: img?.endsWith('/productPlaceholder.svg') ? 'default' : 'available',
    };
  }

  return {};
};

const getSourceCTAProperties = () => {
  const sourceCTA = JSON.parse(SessionStorage.get('SOURCE_CTA') ?? '{}');

  if (sourceCTA) {
    return {
      source_cta: sourceCTA?.title,
    };
  }
  return {};
};

type WebEngageEventOptions = {
  fire: boolean;
  isDirect?: boolean;
};

type FireGTMEventOptions = {
  includeCustomer?: boolean;
  includeSelectedDevice?: boolean;
  includeScreen?: boolean;
  includeSourceCTA?: boolean;
  webengage?: WebEngageEventOptions;
};

const useGTM = () => {
  const { state: profile } = useProfileContext();
  const { state: appConfig } = useAppConfigContext();
  const { state: product } = useProductContext();
  const { fireWebEngageEvent } = useWebEngage();
  const customerProperties = useMemo(() => getCustomerProperties(profile), [profile]);
  const devicesMeta = useMemo(() => {
    if (!customerProperties.logged_in) return {};
    let total_owned_asset_count = 0;
    let total_rented_asset_count = 0;
    let total_ewp_asset_count = 0;
    if (product.list) {
      product.list.forEach(({ isRental, technology }) => {
        if (isRental) total_rented_asset_count++;
        else total_owned_asset_count++;

        if ([DEVICE_TECHNOLOGY.RO, DEVICE_TECHNOLOGY.UV].includes(technology)) {
          total_ewp_asset_count++;
        }
      });
      return {
        total_owned_asset_count,
        total_rented_asset_count,
        total_ewp_asset_count,
        is_multi_asset_user: product.list?.length > 0,
      };
    }

    return {};
  }, [product.list]);

  const fireGTMEvent = useCallback(
    (event: string, properties?: MapType, options: FireGTMEventOptions = {}) => {
      const {
        includeCustomer = true,
        includeScreen = true,
        includeSelectedDevice = true,
        includeSourceCTA = true,
        webengage = {
          fire: false,
          isDirect: false,
        },
      } = options;

      let eventMeta = Object.assign(
        { event, platform: appConfig.isFlutterClient ? PLATFORMS.FLUTTER_WEB_VIEW : PLATFORMS.WEB },
        devicesMeta,
        DEFAULT_EVENT_PROPERTIES,
        properties ?? {},
      );
      if (includeScreen) {
        eventMeta = Object.assign(eventMeta, getScreenProperties());
      }

      if (includeCustomer) {
        eventMeta = Object.assign(eventMeta, customerProperties);
      }
      if (includeSelectedDevice) {
        eventMeta = Object.assign(eventMeta, getSelectedDeviceProperties(product.selectedProduct));
      }

      if (includeSourceCTA) {
        eventMeta = Object.assign(eventMeta, getSourceCTAProperties());
      }
      eventMeta = Object.assign(eventMeta, getTrafficTrackingProperties());
      pushEventToDataLayer(eventMeta);
      // Fire WebEngage Event based on options
      // By default, it will not fire WE event
      if (options?.webengage?.fire) {
        fireWebEngageEvent(eventMeta, { isDirect: webengage.isDirect });
      }
    },
    [product.selectedProduct, devicesMeta, profile],
  );

  return { fireGTMEvent };
};

export default useGTM;
